import { useFormik } from 'formik';
import { Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Header from 'src/components/Headers/Header';
import { FC, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { resetPasswordWithToken } from 'src/services/auth.service';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { routeURL } from 'src/utils/properties.utils';
import { resetPasswordValidationSchema } from 'src/utils/validationScheme.utils';
import TextField from 'src/components/InputFields/TextField';
import {
  AuthFormContainer,
  AuthTitle,
  AuthFieldsContainer,
  AuthButton,
  InstructionText,
  AuthLinkContainer,
  VerificationPageContainer,
} from 'src/components/StyledComponents/StyledAuthPages';
import { ButtonStyled } from 'src/components/StyledComponents/StyledPageComponents';

const ResetPassword: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSnackbar } = StyledSnackbar();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      showSnackbar('Invalid or missing reset link.', 'error');
      navigate(routeURL.FORGOT_PASSWORD);
    }
  }, [token, navigate]);

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: resetPasswordValidationSchema,
    onSubmit: async (values) => {
      if (!token) return;
      setIsSubmitting(true);
      try {
        const response = await resetPasswordWithToken(
          token,
          values.newPassword
        );
        showSnackbar(response.data.message, 'success');
        setTimeout(() => {
          navigate(routeURL.LOGIN);
        }, 2000);
      } catch (e) {
        const errorMessage = await getApiErrorMessage(e);
        showSnackbar(errorMessage, 'error');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  if (!token) {
    return null;
  }

  return (
    <VerificationPageContainer>
      <Header smScreen={smScreen} />

      <AuthFormContainer
        smScreen={smScreen}
        height={400}
        component="form"
        onSubmit={formik.handleSubmit as any}
      >
        <AuthTitle smScreen={smScreen}>Reset Your Password</AuthTitle>

        <AuthFieldsContainer smScreen={smScreen}>
          <InstructionText>
            Please enter your new password below
          </InstructionText>

          <TextField
            id="newPassword"
            label="New Password"
            type="password"
            showPasswordToggle
            formikProps={formik}
          />
          <TextField
            id="confirmPassword"
            label="Confirm Password"
            type="password"
            showPasswordToggle
            formikProps={formik}
          />
        </AuthFieldsContainer>

        <AuthButton smScreen={smScreen}>
          <ButtonStyled fullWidth type="submit" disabled={isSubmitting}>
            Reset Password
          </ButtonStyled>
        </AuthButton>
      </AuthFormContainer>
    </VerificationPageContainer>
  );
};

export default ResetPassword;
